import { UNICODE } from '@shared/models/unicode.model';
import { guards } from '../typeguards.helpers';

const expNumbers = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'] as const;
const superScriptMinus = '⁻';

/**
 * Formats a part of a scientific number into a string for standard notation
 * @param part - The part to format
 * @returns The formatted part
 */
const standardPartFormatter = (part: Intl.NumberFormatPart): string => {
  switch (part.type) {
    case 'decimal':
      return '.';
    case 'minusSign':
      return UNICODE.MATH.MINUS;
    case 'exponentSeparator':
      return ' × 10'.replaceAll(' ', UNICODE.SPACES.NARROW_NON_BREAKING);
    case 'exponentMinusSign':
      return superScriptMinus;
    case 'exponentInteger':
      return part.value
        .split('')
        .map((c) => expNumbers[Number(c)] ?? c)
        .join('');
    default:
      return part.value;
  }
};
/**
 * Formats a part of a scientific number into a string for compact notation
 * @param part - The part to format
 * @returns The formatted part
 */
const compactPartFormatter = (part: Intl.NumberFormatPart): string => {
  switch (part.type) {
    case 'decimal':
      return '.';
    case 'minusSign':
      return UNICODE.MATH.MINUS;
    case 'exponentSeparator':
      return part.value.toLowerCase();
    default:
      return part.value;
  }
};

type ScientificFormatOptions = {
  notation?: 'compact' | 'standard';
  decimals?: number;
};
const defaultScientificFormatOptions: Required<ScientificFormatOptions> = {
  notation: 'standard',
  decimals: 2,
};

/**
 * Formats a number into scientific notation like: "1.34 × 10³"
 */
export const getScientificDisplayNumber = (
  value: number,
  {
    decimals = 2,
    notation = 'standard',
  }: ScientificFormatOptions = defaultScientificFormatOptions,
): string => {
  try {
    if (!guards.is.finiteNumber(value)) return String(value);
    if (value === 0) return '0';

    const formattedParts = new Intl.NumberFormat('en-US', {
      notation: 'scientific',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).formatToParts(value);
    switch (notation) {
      case 'compact':
        return formattedParts.map(compactPartFormatter).join('');
      case 'standard':
        return formattedParts.map(standardPartFormatter).join('');
    }
  } catch {
    console.error('Error formatting scientific number', value, decimals);
    return String(value);
  }
};
